import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler } from '../api/client.ts';
import { useSession } from '../composants/session.tsx';
import { Entete } from '../composants/mise-en-page.tsx';
import { Carte, Chargement, Erreur, Etiquette, Vide } from '../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf } from '../composants/ui/index.ts';
import { useApercuPdf } from '../composants/apercu-pdf.tsx';
import { FormulaireBoutique } from './boutiques/FormulaireBoutique.tsx';

/**
 * Boutiques — RG-012 à RG-018.
 *
 * Le code n'est PAS modifiable : il entre dans la numérotation de tous les
 * documents (`COT1-VTE-2026-000042`). L'écran l'affiche en lecture seule
 * plutôt que de proposer un champ que le serveur refuserait.
 */

interface Boutique {
  id: string;
  code: string;
  nom: string;
  adresse: string | null;
  active: boolean;
  /** Dépôt central : il reçoit et répartit, il ne vend jamais (RG-192). */
  est_depot: boolean;
}

interface Detail extends Boutique {
  session_caisse_obligatoire: boolean;
  plafond_remise_vendeur_pct: string;
  seuil_ecart_caisse: number;
  seuil_ecart_inventaire_pct: string;
  photo_panier_obligatoire: boolean;
}

export function Boutiques(): ReactNode {
  const { peut } = useSession();
  const client = useQueryClient();
  const { ouvrir } = useApercuPdf();
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);

  /**
   * Activation et desactivation — RG-014, RG-015.
   *
   * Le serveur refuse de fermer une boutique dont une caisse est ouverte ou
   * dont une marchandise est en transit, et le dit avec les chiffres. On
   * n'anticipe pas ce controle ici : le duplique donnerait deux verites a
   * maintenir, et c'est celle du serveur qui compte.
   */
  const activite = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      appeler(`/boutiques/${id}/${active ? 'reactivation' : 'desactivation'}`, {
        methode: 'POST',
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['boutiques-admin'] });
      void client.invalidateQueries({ queryKey: ['boutiques'] });
    },
  });

  const { data, isPending, error } = useQuery({
    queryKey: ['boutiques-admin'],
    queryFn: () => appeler<{ donnees: Boutique[] }>('/boutiques'),
    select: (reponse) => reponse.donnees,
  });

  return (
    <>
      <Entete
        titre="Boutiques"
        sous="Le code d’une boutique est immuable : il numérote ses documents."
        actions={
          peut('boutique.creer') && !creation ? (
            <button
              type="button"
              onClick={() => {
                setCreation(true);
              }}
            >
              Nouvelle boutique
            </button>
          ) : undefined
        }
      />

      <Erreur erreur={error} />
      <Erreur erreur={activite.error} />

      <div className="pile">
        {creation && (
          <FormulaireBoutique
            onFerme={() => {
              setCreation(false);
            }}
          />
        )}

        {ouverte !== null && (
          <Parametres
            boutiqueId={ouverte}
            modifiable={peut('boutique.parametrer')}
            onFerme={() => {
              setOuverte(null);
            }}
            onEnregistre={() => {
              void client.invalidateQueries({ queryKey: ['boutiques-admin'] });
            }}
          />
        )}

        <Carte>
          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucune boutique dans votre portée." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Boutiques',
                        nomFichier: 'boutiques',
                        colonnes: [
                          { cle: 'code', entete: 'Code', rendu: () => null, texte: (b: Boutique) => b.code },
                          { cle: 'nom', entete: 'Nom', rendu: () => null, texte: (b: Boutique) => b.nom },
                          {
                            cle: 'adresse',
                            entete: 'Adresse',
                            rendu: () => null,
                            texte: (b: Boutique) => b.adresse ?? '—',
                          },
                          {
                            cle: 'etat',
                            entete: 'État',
                            rendu: () => null,
                            texte: (b: Boutique) => (b.active ? 'Active' : 'Désactivée'),
                          },
                          {
                            cle: 'depot',
                            entete: 'Dépôt central',
                            rendu: () => null,
                            texte: (b: Boutique) => (b.est_depot ? 'Oui' : 'Non'),
                          },
                        ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Nom</th>
                    <th>Adresse</th>
                    <th>État</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((boutique) => (
                    <tr key={boutique.id}>
                      <td className="mono">{boutique.code}</td>
                      <td>
                        {boutique.nom}
                        {/* Un dépôt n'apparaît pas dans les écrans de vente :
                            le signaler ici évite de chercher pourquoi. */}
                        {boutique.est_depot && <span> — dépôt central</span>}
                      </td>
                      <td>{boutique.adresse ?? '—'}</td>
                      <td>
                        {boutique.active ? (
                          <Etiquette ton="vert" texte="active" />
                        ) : (
                          <Etiquette ton="rouge" texte="désactivée" />
                        )}
                      </td>
                      <td>
                        <div className="ligne">
                          <button
                            type="button"
                            className="bouton--discret"
                            onClick={() => {
                              setOuverte(boutique.id);
                            }}
                          >
                            Paramètres
                          </button>
                          {peut('boutique.desactiver') && (
                            <button
                              type="button"
                              className="bouton--discret"
                              disabled={activite.isPending}
                              onClick={() => {
                                activite.mutate({
                                  id: boutique.id,
                                  active: !boutique.active,
                                });
                              }}
                            >
                              {boutique.active ? 'Désactiver' : 'Réactiver'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}

function Parametres({
  boutiqueId,
  modifiable,
  onFerme,
  onEnregistre,
}: {
  boutiqueId: string;
  modifiable: boolean;
  onFerme: () => void;
  onEnregistre: () => void;
}): ReactNode {
  const client = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: ['boutique', boutiqueId],
    queryFn: () => appeler<Detail>(`/boutiques/${boutiqueId}`),
  });

  const mutation = useMutation({
    mutationFn: (corps: Record<string, unknown>) =>
      appeler<Detail>(`/boutiques/${boutiqueId}`, { methode: 'PATCH', corps }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['boutique', boutiqueId] });
      onEnregistre();
    },
  });

  if (isPending || data === undefined) {
    return (
      <Carte>
        <Chargement />
      </Carte>
    );
  }

  return (
    <Carte
      titre={`Paramètres — ${data.code}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <div className="champs">
        <div className="champ">
          <label htmlFor="code-lecture">Code (immuable)</label>
          <input id="code-lecture" value={data.code} readOnly disabled />
        </div>
        <div className="champ">
          <label htmlFor="nom-boutique">Nom</label>
          <input
            id="nom-boutique"
            defaultValue={data.nom}
            disabled={!modifiable}
            onBlur={(evenement) => {
              if (evenement.target.value !== data.nom) {
                mutation.mutate({ nom: evenement.target.value });
              }
            }}
          />
        </div>
      </div>

      <fieldset style={{ border: 0, padding: 0, margin: '12px 0 0' }}>
        <legend className="indicateur__libelle">Règles d’exploitation</legend>

        <label style={{ marginTop: 10 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 8 }}
            disabled={!modifiable}
            checked={data.session_caisse_obligatoire}
            onChange={(evenement) => {
              mutation.mutate({ sessionCaisseObligatoire: evenement.target.checked });
            }}
          />
          Session de caisse obligatoire pour vendre
        </label>

        <label style={{ marginTop: 10 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 8 }}
            disabled={!modifiable}
            checked={data.est_depot}
            onChange={(evenement) => {
              mutation.mutate({ estDepot: evenement.target.checked });
            }}
          />
          Dépôt central : approvisionne les boutiques, ne vend pas
        </label>

        <label style={{ marginTop: 10 }}>
          <input
            type="checkbox"
            style={{ width: 'auto', marginRight: 8 }}
            disabled={!modifiable}
            checked={data.photo_panier_obligatoire}
            onChange={(evenement) => {
              mutation.mutate({ photoPanierObligatoire: evenement.target.checked });
            }}
          />
          Photo du panier obligatoire pour valider une vente
        </label>

        <div className="champs" style={{ marginTop: 14 }}>
          <div className="champ">
            <label htmlFor="plafond-remise">Plafond de remise vendeur (%)</label>
            <input
              id="plafond-remise"
              type="number"
              min={0}
              max={100}
              step={0.5}
              disabled={!modifiable}
              defaultValue={data.plafond_remise_vendeur_pct}
              onBlur={(evenement) => {
                mutation.mutate({
                  plafondRemiseVendeurPct: Number(evenement.target.value),
                });
              }}
            />
          </div>
          <div className="champ">
            <label htmlFor="seuil-caisse">Seuil d’écart de caisse (FCFA)</label>
            <input
              id="seuil-caisse"
              type="number"
              min={0}
              step={100}
              disabled={!modifiable}
              defaultValue={data.seuil_ecart_caisse}
              onBlur={(evenement) => {
                mutation.mutate({ seuilEcartCaisse: Number(evenement.target.value) });
              }}
            />
          </div>
          <div className="champ">
            <label htmlFor="seuil-inventaire">Seuil d’écart d’inventaire (%)</label>
            <input
              id="seuil-inventaire"
              type="number"
              min={0}
              max={100}
              step={0.5}
              disabled={!modifiable}
              defaultValue={data.seuil_ecart_inventaire_pct}
              onBlur={(evenement) => {
                mutation.mutate({
                  seuilEcartInventairePct: Number(evenement.target.value),
                });
              }}
            />
          </div>
        </div>
      </fieldset>

      <Erreur erreur={mutation.error} />
      {mutation.isPending && <div className="entete__sous">Enregistrement…</div>}
    </Carte>
  );
}
